import { cn } from '@/lib/utils';
import L, { LatLngExpression } from 'leaflet';
import { useEffect, useMemo } from 'react';
import { MapContainer, Polygon, TileLayer, Tooltip } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GeoJSONFeature, GeoJSONFeatureCollection } from './map.types';

type Props = {
  zoom: number;
  data: GeoJSONFeatureCollection;
  isFarmMap?: boolean;
};

export const FarmMap = ({ zoom, data, isFarmMap }: Props) => {
  const navigate = useNavigate();

  const renderPolygon = (feature: GeoJSONFeature) => {
    const { geometry, properties } = feature;

    if (!geometry || !geometry.coordinates) return null;

    const styles = {
      color: properties.stroke || '#000000',
      fillColor: properties.fill || '#808080',
      fillOpacity: 0.5,
      weight: properties['stroke-width'] || 2,
    };

    let positions: LatLngExpression[][] | LatLngExpression[][][];
    const eventHandlers = isFarmMap
      ? {
          click: () => navigate(`fields/${properties.agri_field_id}`),
        }
      : undefined;

    if (geometry.type === 'Polygon') {
      positions = (geometry.coordinates as number[][][]).map((ring) =>
        (ring as number[][]).map((coord) => [coord[1], coord[0]] as LatLngExpression)
      );
    } else if (geometry.type === 'MultiPolygon') {
      positions = (geometry.coordinates as number[][][][]).map((polygon) =>
        (polygon as number[][][]).map((ring) => (ring as number[][]).map((coord) => [coord[1], coord[0]] as LatLngExpression))
      );
    } else {
      return null;
    }

    return (
      <Polygon key={feature.id} pathOptions={styles} positions={positions} eventHandlers={eventHandlers}>
        <Tooltip sticky>
          <div className={cn('bg-white p-2 rounded')}>
            <strong>Field ID:</strong> {properties.field_id}
            {properties.field_num && (
              <>
                <br />
                <strong>Field Number:</strong> {properties.field_num}
              </>
            )}
          </div>
        </Tooltip>
      </Polygon>
    );
  };

  const polygons = useMemo(() => {
    if (!data.features?.length) {
      return <p className={cn('text-center text-white')}>No data available</p>;
    }
    return <>{data.features.map(renderPolygon)}</>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.features]);

  const bounds = useMemo(() => {
    if (data) {
      const bounds = L.geoJSON(data).getBounds();
      if (bounds.isValid()) {
        return { status: 'valid', data: bounds };
      }
      return { status: 'invalid', error: { message: 'Invalid bounds' } };
    }
    return { status: 'loading', error: { message: 'No data' } };
  }, [data]);

  useEffect(() => {
    if (bounds.status === 'invalid') {
      console.log('Invalid bounds:', bounds.status);
      toast.error('Cannot display map - invalid geo json.', {
        autoClose: 3000,
      });
    }
  }, [bounds.status]);

  return (
    <div className={cn('h-full w-full')}>
      <MapContainer
        zoom={zoom}
        center={data.centroid}
        bounds={bounds.data}
        scrollWheelZoom={false}
        className={cn('flex flex-grow relative w-full aspect-square max-h-[615px] my-5')}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.arcgis.com/">Esri</a>'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        {polygons}
      </MapContainer>
    </div>
  );
};
